import type { SocialUser } from '@features/social/api/social.service.types';
import type { IdentityMode } from '@features/tenant/api/tenant.service';
import type { CurrentUserResponse } from './api/auth.service.types';

export interface CurrentPlayer {
  playerId: string;
  operationId: string;
  identityMode: IdentityMode;
  socialProfile?: SocialUser;
}

interface CurrentPlayerTenant {
  status: 'resolving' | 'ready' | 'unknown-host' | 'unavailable';
  operationId: string;
  identityMode: IdentityMode | '';
}

const objectIdPattern = /^[0-9a-f]{24}$/i;
const zeroObjectId = '000000000000000000000000';

const validObjectId = (value: unknown): value is string =>
  typeof value === 'string' &&
  objectIdPattern.test(value) &&
  value !== zeroObjectId;

export function currentPlayerFrom(
  currentUser: CurrentUserResponse,
  tenant: CurrentPlayerTenant,
  socialProfile?: SocialUser,
): CurrentPlayer | null {
  if (
    tenant.status !== 'ready' ||
    !validObjectId(tenant.operationId) ||
    (tenant.identityMode !== 'managed' &&
      tenant.identityMode !== 'delegated') ||
    !validObjectId(currentUser.id)
  ) {
    return null;
  }

  const canonicalOperationId = currentUser.operation_id;
  const legacyOperationId = currentUser.operationId;
  if (
    (canonicalOperationId !== undefined &&
      legacyOperationId !== undefined &&
      canonicalOperationId !== legacyOperationId) ||
    !validObjectId(canonicalOperationId ?? legacyOperationId) ||
    (canonicalOperationId ?? legacyOperationId) !== tenant.operationId
  ) {
    return null;
  }

  const player: CurrentPlayer = {
    playerId: currentUser.id,
    operationId: tenant.operationId,
    identityMode: tenant.identityMode,
  };
  if (socialProfile?._id === player.playerId) {
    player.socialProfile = socialProfile;
  }
  return player;
}
